import BaseApiService from './BaseApiService';

/**
 * Video API Service
 * 
 * Handles video management operations including:
 * - Video creation and editing
 * - Video listing and retrieval
 * - Video materials and preferences
 * - Video validation and analytics
 * 
 * @class VideoApiService
 * @extends BaseApiService
 */
class VideoApiService extends BaseApiService {
  /**
   * Gets list of all videos (admin view)
   * @returns {Promise<Array>} List of all videos with management info
   */
  async getVideoList() {
    return await this.get('/video/list');
  }

  /**
   * Gets available video materials
   * @returns {Promise<Array>} List of available materials
   */
  async getVideoMaterials() {
    return await this.get('/video/materials');
  }

  /**
   * Creates or updates a video
   * @param {Object} videoData - Video data
   * @param {string|null} videoData.id - Video ID (null for new video)
   * @param {string} videoData.urlEnglish - English video URL
   * @param {string} videoData.externalIdEnglish - English external ID
   * @param {string} videoData.titleEnglish - English title
   * @param {string} videoData.descriptionEnglish - English description
   * @param {string} videoData.url - German video URL
   * @param {string} videoData.externalId - German external ID
   * @param {string} videoData.title - German title
   * @param {string} videoData.description - German description
   * @param {Array<number>} videoData.preferenceIds - Preference IDs
   * @param {Array<number>} videoData.materials - Material IDs
   * @returns {Promise<Object>} Created/updated video data
   */
  async saveVideo(videoData) {
    const payload = {
      id: videoData.id || null,
      urlEnglish: videoData.urlEnglish,
      externalIdEnglish: videoData.externalIdEnglish,
      titleEnglish: videoData.titleEnglish,
      descriptionEnglish: videoData.descriptionEnglish,
      url: videoData.url,
      externalId: videoData.externalId,
      title: videoData.title,
      description: videoData.description,
      preferenceIds: videoData.preferenceIds || [],
      materials: videoData.materials || [],
    };

    return await this.post('/video', payload);
  }

  /**
   * Gets video by ID with full details
   * @param {string} videoId - Video ID
   * @returns {Promise<Object>} Video details
   */
  async getVideoById(videoId) {
    return await this.get(`/video/${videoId}`);
  }

  /**
   * Deletes a video
   * @param {string} videoId - Video ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteVideo(videoId) {
    return await this.delete(`/video/${videoId}`);
  }

  /**
   * Validates video data before saving
   * @param {Object} videoData - Video data to validate
   * @returns {Object} Validation result
   */
  validateVideoData(videoData) {
    const errors = [];

    // Check required English fields
    if (!videoData.urlEnglish?.trim()) {
      errors.push('English video URL is required');
    }

    if (!videoData.externalIdEnglish?.trim()) {
      errors.push('English external ID is required');
    }

    if (!videoData.titleEnglish?.trim()) {
      errors.push('English title is required');
    }

    if (!videoData.descriptionEnglish?.trim()) {
      errors.push('English description is required');
    }

    // Validate URL formats (basic validation)
    if (videoData.urlEnglish && !this.isValidVideoUrl(videoData.urlEnglish)) {
      errors.push('English video URL format is invalid');
    }

    if (videoData.url && !this.isValidVideoUrl(videoData.url)) {
      errors.push('German video URL format is invalid');
    }

    // Check for preference selection
    if (!videoData.preferenceIds?.length) {
      errors.push('At least one preference must be selected');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: this.getValidationWarnings(videoData)
    };
  }

  /**
   * Gets validation warnings for video data
   * @param {Object} videoData - Video data
   * @returns {Array} List of warnings
   */
  getValidationWarnings(videoData) {
    const warnings = [];

    // Check for missing German fields
    if (!videoData.url?.trim()) {
      warnings.push('German video URL is missing - video will only be available in English');
    }

    if (!videoData.title?.trim()) {
      warnings.push('German title is missing - English title will be used');
    }

    if (!videoData.description?.trim()) {
      warnings.push('German description is missing - English description will be used');
    }

    // Check for missing materials
    if (!videoData.materials?.length) {
      warnings.push('No materials selected - consider adding relevant materials');
    }

    return warnings;
  }

  /**
   * Basic video URL validation
   * @param {string} url - Video URL to validate
   * @returns {boolean} Whether URL appears to be valid
   */
  isValidVideoUrl(url) {
    if (!url) return false;
    
    // Basic validation - could be enhanced based on supported platforms
    const videoUrlPattern = /^https?:\/\/.+\.(mp4|webm|ogg|mov|avi|mkv)$/i;
    const vimeoPattern = /^https?:\/\/(www\.)?(vimeo\.com|player\.vimeo\.com)/i;
    const youtubePattern = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i;
    
    return videoUrlPattern.test(url) || vimeoPattern.test(url) || youtubePattern.test(url);
  }

  /**
   * Extracts video ID from URL
   * @param {string} url - Video URL
   * @returns {string|null} Extracted video ID or null
   */
  extractVideoId(url) {
    if (!url) return null;

    // Vimeo ID extraction
    const vimeoMatch = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
    if (vimeoMatch) return vimeoMatch[1];

    // YouTube ID extraction
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    if (youtubeMatch) return youtubeMatch[1];

    return null;
  }

  /**
   * Gets video thumbnail URL
   * @param {string} externalId - Video external ID
   * @param {string} platform - Video platform ('vimeo', 'youtube', etc.)
   * @returns {string} Thumbnail URL
   */
  getVideoThumbnail(externalId, platform = 'vimeo') {
    switch (platform.toLowerCase()) {
      case 'vimeo':
        return `https://vumbnail.com/${externalId}.jpg`;
      case 'youtube':
        return `https://img.youtube.com/vi/${externalId}/hqdefault.jpg`;
      default:
        return `https://vumbnail.com/${externalId}.jpg`;
    }
  }

  /**
   * Checks if video has missing required fields
   * @param {Object} video - Video object
   * @returns {boolean} Whether video has missing fields
   */
  hasMissingRequiredFields(video) {
    return !video.urlEnglish || 
           !video.externalIdEnglish || 
           !video.titleEnglish || 
           !video.descriptionEnglish;
  }

  /**
   * Gets video upload statistics
   * @returns {Promise<Object>} Upload statistics
   */
  async getVideoStats() {
    return await this.get('/video/stats');
  }

  /**
   * Bulk updates video data
   * @param {Array} videos - Array of video objects to update
   * @returns {Promise<Object>} Bulk update result  
   */
  async bulkUpdateVideos(videos) {
    return await this.post('/video/bulk-update', { videos });
  }

  /**
   * Gets video activity log
   * @param {string} videoId - Video ID
   * @returns {Promise<Array>} Activity log entries
   */
  async getVideoActivityLog(videoId) {
    return await this.get(`/video/${videoId}/activity`);
  }

  /**
   * Duplicates a video
   * @param {string} videoId - Video ID to duplicate
   * @param {Object} overrides - Fields to override in the duplicate
   * @returns {Promise<Object>} Duplicated video data
   */
  async duplicateVideo(videoId, overrides = {}) {
    return await this.post(`/video/${videoId}/duplicate`, overrides);
  }
}

// Create and export singleton instance
const videoApiService = new VideoApiService();
export default videoApiService; 